import { PropertyTypeEnum } from '@salescore/client-api'
import {
  CustomModelOrSnapshotIcon,
  getProviderFromModelName,
  HUB_PROPERTY_TYPE_LABEL,
  SourceProviderIcon,
} from '@salescore/client-common'
import type { CoreModel, ModelProperty } from '@salescore/core'
import { isCustomModel, isSnapshotModel } from '@salescore/features'
import { Cascader } from 'antd'
import { Array, Order, pipe, Record } from 'effect'
import { make } from 'effect/Order'
import { t } from 'i18next'
import { type ReactElement, useMemo } from 'react'

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function PropertyCascader({
  models,
  placeholder,
  className,
  onChange,
}: {
  models: CoreModel[]
  placeholder?: string
  className?: string
  onChange: (x: { propertyName: string; modelName: string }) => void
}): ReactElement {
  const modelPropertiesOrder = useMemo(
    () =>
      make((a: { label: string }, b: { label: string }) => Order.Number(rankTypeName(b.label), rankTypeName(a.label))),
    [],
  )
  const options = useMemo(
    () =>
      pipe(
        models,
        Array.sortBy(Order.mapInput(Order.String, (x) => x.name)),
        Array.filter((x) => !x.name.startsWith(`salescore_`)),
        Array.map((model) => {
          const provider = getProviderFromModelName(model.name)
          return {
            value: model.name,
            label: (
              <span className="whitespace-normal break-all">
                {isCustomModel(model) || isSnapshotModel(model) ? (
                  <CustomModelOrSnapshotIcon model={model} />
                ) : (
                  <SourceProviderIcon provider={provider} />
                )}{' '}
                {model.label}
              </span>
            ),
            children: pipe(
              model.properties,
              Array.groupBy((x) => propertyToTypeName(x)),
              Record.toEntries,
              Array.map(([typeJa, ps]) => ({
                value: typeJa,
                label: typeJa,
                // eslint-disable-next-line max-nested-callbacks
                children: ps.map((property) => ({
                  value: property.name,
                  label: property.label,
                  // searchKey: [model.name, property.name].join(' ')
                })),
              })),
              Array.sort(modelPropertiesOrder),
            ),
          }
        }),
      ),
    [modelPropertiesOrder, models],
  )

  return (
    <Cascader
      className={className}
      style={{ width: '100%' }}
      expandTrigger="hover"
      options={options}
      showSearch={{
        filter: (inputValue, optionAsList) => {
          // const searchKeyword = optionAsList[2]?.searchKey ?? ''
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          const searchKeyword = [optionAsList[0]?.label ?? '', optionAsList[2]?.label ?? ''].join(' ')

          return inputValue.split(/\s/v).every((input) => searchKeyword.includes(input.toLowerCase()))
        },
      }}
      placeholder={placeholder ?? t(`選択してください`)}
      onChange={(path: unknown[]) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-non-null-assertion
        const modelName = path[0]! as string
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-non-null-assertion
        const propertyName = path[2]! as string
        onChange({ modelName, propertyName })
      }}
      value={undefined} // 現状のユースケースだと常に何も表示しない想定。必要があれば改修
      displayRender={(labels, selectedOptions) => <></>}
      // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- property-cascader は antd.less にスタイル定義あり
      popupRender={(menus) => <div className="property-cascader">{menus}</div>}
    />
  )
}

function propertyToTypeName(property: ModelProperty) {
  if ((property.selectOptions ?? []).length > 0) {
    return t('選択肢')
  }
  if ((property.referenceTo ?? []).length > 0) {
    return t('参照ID')
  }

  if (property.type === 'integer' || property.type === 'numeric') {
    return t('数値')
  }
  if (property.type === PropertyTypeEnum.Date || property.type === PropertyTypeEnum.Datetime) {
    return t('日付')
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return HUB_PROPERTY_TYPE_LABEL[property.type] ?? property.type
}

// XXX: ラベル値に依存した並び替えになっていてあまりよくないが、現状これで問題ないのと、今後の改修でデグレしたとしても大きな問題にならないのでこれで
function rankTypeName(typeName: string): number {
  return (
    {
      [t('テキスト')]: 10,
      [t('選択肢')]: 9,
      [t('数値')]: 8,
      [t('日付')]: 7,
      [t('参照ID')]: -1,
    }[typeName] ?? 0
  )
}
